import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface School {
  id: string;
  name: string;
  location: string;
  price: number;
  colors: string[];
}

const SchoolSelection: React.FC = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/schools`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch schools');
        }
        
        const result = await response.json();
        
        if (result.success) {
          setSchools(result.data.schools);
        } else {
          throw new Error(result.error || 'Failed to load schools');
        }
      } catch (err) {
        console.error('Error fetching schools:', err);
        setError(err instanceof Error ? err.message : 'Failed to load schools');
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  const handleSchoolSelect = (schoolId: string) => {
    navigate(`/schools/${schoolId}/occasions`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-white mb-4">Select Your School</h1>
            <p className="text-lg text-gray-300">Loading schools...</p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-goldie"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-midnight py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-white mb-4">Select Your School</h1>
            <p className="text-lg text-red-400 mb-4">Error: {error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-goldie text-black px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-white mb-4">Select Your School</h1>
          <p className="text-lg text-gray-300">Choose your school to see available occasions and pricing</p>
        </div>
        
        {schools.length === 0 ? (
          <div className="text-center">
            <p className="text-lg text-gray-300 mb-4">No schools are currently available.</p>
            <p className="text-gray-400">Please check back later or contact us if you don't see your school.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schools.map((school) => (
              <div 
                key={school.id}
                className="card-hover cursor-pointer bg-sky border-2 border-black p-6 hover:border-white transition-colors duration-200"
                style={{
                  borderTop: `4px solid ${school.colors[0]}`,
                  borderRight: `4px solid ${school.colors[1]}`,
                  borderBottom: `4px solid ${school.colors[2]}`,
                  borderLeft: `4px solid ${school.colors[0]}`
                }}
                onClick={() => handleSchoolSelect(school.id)}
              >
                <h3 className="text-xl font-semibold text-black mb-2">{school.name}</h3>
                <p className="text-gray-700 mb-4">{school.location}</p>
                <p className="text-black font-semibold">${school.price} per video</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolSelection; 